import { useState } from "react";
import { ArrowRight, BadgeCheck, Star } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import { ClutchWidget } from "@/components/home/ClutchWidget";
import { REVIEWS, REVIEWS_META } from "@/lib/reviews";

type ReviewsCopy = {
  eyebrow: string;
  title: string;
  caption: string;
  showAll: string;
  showLess: string;
};

export function TestimonialsSection({ reviews }: { reviews: ReviewsCopy }) {
  const [reviewsExpanded, setReviewsExpanded] = useState(false);
  const lead = REVIEWS[4] ?? REVIEWS[0];
  const supporting = REVIEWS.filter((review) => review !== lead).slice(0, 2);
  const archive = REVIEWS.filter((review) => review !== lead && !supporting.includes(review));

  return (
    <div>
      <Reveal>
        <div className="grid gap-8 border-b border-border pb-10 lg:grid-cols-12 lg:items-end">
          <div className="lg:col-span-8">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
              <div role="img" className="flex items-center gap-1" aria-label={`${REVIEWS_META.rating} out of 5`}>
                {Array.from({ length: 5 }).map((_, index) => (
                  <Star key={index} className="h-4 w-4 fill-amber-400 text-amber-400" aria-hidden="true" />
                ))}
              </div>
              <span className="font-display text-lg font-semibold text-foreground">{REVIEWS_META.rating}</span>
              <span className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">{reviews.eyebrow}</span>
              <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-foreground">
                <BadgeCheck className="h-3.5 w-3.5 text-amber-500" aria-hidden="true" />
                Verified Shopify Partner
              </span>
            </div>
            <h2 className="mt-5 max-w-3xl font-display text-4xl font-semibold leading-[1.04] tracking-tight text-foreground sm:text-5xl lg:text-6xl">
              {reviews.title}
            </h2>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <a
              href={REVIEWS_META.partnerUrl}
              target="_blank"
              rel="noreferrer noopener"
              className="group inline-flex items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
            >
              {reviews.caption.replace("{count}", String(REVIEWS_META.count))}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
            </a>
          </div>
        </div>
      </Reveal>

      {lead && (
        <div className="grid gap-10 py-12 lg:grid-cols-12 lg:gap-16">
          <Reveal className="lg:col-span-8">
            <figure>
              <blockquote className="max-w-4xl font-display text-3xl font-medium leading-[1.16] tracking-tight text-foreground sm:text-4xl lg:text-5xl">
                “{lead.quote}”
              </blockquote>
              <figcaption className="mt-7 border-l border-amber-400 pl-4">
                <p className="font-display text-base font-semibold text-foreground">{lead.brand}</p>
                <p className="mt-1 text-[10px] uppercase tracking-[0.22em] text-muted-foreground">{lead.category}</p>
              </figcaption>
            </figure>
          </Reveal>

          <div className="divide-y divide-border border-y border-border lg:col-span-4">
            {supporting.map((review, index) => (
              <Reveal key={review.brand} delay={(index + 1) * 70}>
                <figure className="py-7">
                  <blockquote className="text-sm leading-relaxed text-foreground">“{review.quote}”</blockquote>
                  <figcaption className="mt-5">
                    <p className="font-display text-sm font-semibold text-foreground">{review.brand}</p>
                    <p className="mt-1 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{review.category}</p>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      )}

      {reviewsExpanded && (
        <div className="grid border-t border-border md:grid-cols-2">
          {archive.map((review, index) => (
            <Reveal
              key={`${review.brand}-${index}`}
              delay={(index % 6) * 35}
              className="border-b border-border py-7 md:odd:pr-8 md:even:border-l md:even:pl-8"
            >
              <figure>
                <blockquote className="text-sm leading-relaxed text-foreground/90">“{review.quote}”</blockquote>
                <figcaption className="mt-4 flex flex-wrap items-baseline gap-x-2 gap-y-1">
                  <span className="font-display text-sm font-semibold text-foreground">{review.brand}</span>
                  <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{review.category}</span>
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      )}

      <Reveal>
        <div className="mt-8 flex flex-col items-center gap-4">
          {archive.length > 0 && (
            <button
              type="button"
              onClick={() => setReviewsExpanded((value) => !value)}
              className="inline-flex items-center gap-2 border-b border-foreground/30 pb-1 text-sm font-medium text-foreground transition-colors hover:border-foreground"
            >
              {reviewsExpanded
                ? reviews.showLess
                : reviews.showAll.replace("{count}", String(REVIEWS.length))}
            </button>
          )}

          <div className="relative h-[38px] w-[220px] overflow-visible opacity-70 transition-opacity hover:opacity-100" role="group" aria-label="Clutch reviews">
            <div
              className="absolute left-1/2 top-0 w-[300px]"
              style={{ transform: "translateX(-50%) scale(0.7)", transformOrigin: "top center" }}
            >
              <ClutchWidget />
            </div>
          </div>
        </div>
      </Reveal>
    </div>
  );
}
